/**
 * returning.tsx — returning patients on the board and in the drawer
 * (2026-09-26, returning patients S4.5; CTO Q5/Q9, plan Addenda 2, 3 and 8).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * WHAT THIS RENDERS, all of it from clinic-service S4.1 (#224) card fields:
 *  - the returning-patient CHIPS ("Returning · 2nd attempt", "New order",
 *    "Already on an active plan", "Plan status unknown", "Two live cards",
 *    "Started again"), in the server's order and in the SERVER'S WORDS;
 *  - the "Submitted as …" line, with the visible qualifier "(from this
 *    questionnaire, not verified)" — never a hover-only tooltip (WCAG 1.4.13);
 *  - the "Updated … · created …" card footer;
 *  - the SUPERSEDED surface — a card the SYSTEM closed because the patient
 *    submitted a newer questionnaire — kept visibly apart from a STAFF
 *    duplicate: different fact, different words, different undo.
 *
 * ⚠️ THE LABEL IS THE SERVER'S AND IT IS RENDERED VERBATIM. clinic composes
 * every chip's `label` and appends "(email match — not verified)" when the chip
 * rests on an email-only match (Addendum 2), precisely so that no screen can
 * render the claim without the warning. This module never rebuilds a sentence
 * from `code` + `attemptNumber`; `code` and `basis` pick the chip's LOOK only,
 * and an unknown code still renders its label (a newer server's chip is a
 * truth this build has not heard of, not an error).
 *
 * ⚠️ ABSENT IS ITS OWN ANSWER (R41, the estate's additive posture). A
 * clinic-service that predates S4.1 sends no `returning`, no `lastActivityAt`
 * and no `supersededByDealId`. That renders NO chip (never "first attempt"),
 * the old "Created …" footer, and no superseded marker — because "this server
 * does not say" is not "this patient is new".
 *
 * ⚠️ `activePlan: 'unknown'` IS "NOT KNOWN", NEVER "NO PLAN" (Addendum 8).
 * clinic already turns it into the "Plan status unknown" chip; nothing here
 * ever writes "no active plan" for any value, including `none` (a chip that
 * says nothing is the right rendering of "no").
 *
 * MONEY: none. The chips are money-free by construction (Q9: "flag it to
 * clinicians AND admins"), so both roles render the same thing.
 *
 * R19: nothing here logs. The "Submitted as" name is a staff-only render of a
 * field the same staff already hold on the card.
 */
import type { CSSProperties } from 'react';
import type {
  DealCard,
  DealMatchBasis,
  DealReturning,
  DealReturningChip,
  DealReturningChipCode,
} from '../api/types';
import { formatUtcDate } from '../lib/format';
import { Alert, Button } from '../ui/kit';
import { DealLinkButton } from './duplicateSurface';
import { chipStyle } from './statusChips';

/* ── The chips ─────────────────────────────────────────────────────────────── */

/**
 * The chip's look by code. `active_plan` and `two_live_cards` carry the amber:
 * both say "look before you act on this card" (a second plan could bill beside
 * the first; two live cards can mean two holds). Everything else is quiet —
 * a returning patient is information, not a problem. Text always carries the
 * meaning; the colour never does alone (WCAG 1.4.1).
 */
const CHIP_TONE: Record<DealReturningChipCode, { bg: string; text: string }> = {
  returning: { bg: 'var(--gps-surface-2)', text: 'var(--gps-text)' },
  new_order: { bg: 'var(--gps-surface-2)', text: 'var(--gps-text)' },
  active_plan: { bg: 'var(--gps-warn-bg)', text: 'var(--gps-warn-text)' },
  active_plan_unknown: { bg: 'var(--gps-surface-2)', text: 'var(--gps-muted)' },
  two_live_cards: { bg: 'var(--gps-warn-bg)', text: 'var(--gps-warn-text)' },
  started_again: { bg: 'var(--gps-surface-2)', text: 'var(--gps-text)' },
};
const UNKNOWN_CHIP_TONE = { bg: 'var(--gps-surface-2)', text: 'var(--gps-text)' };

/** The chips a card carries — `[]` for an absent block (an older clinic-service). */
export function returningChips(card: Pick<DealCard, 'returning'>): DealReturningChip[] {
  const chips = card.returning?.chips;
  return Array.isArray(chips) ? chips.filter((chip) => typeof chip?.label === 'string' && chip.label.trim() !== '') : [];
}

/**
 * ONE CHIP. Sentence case, not the tag row's uppercase: these are sentences
 * ("Returning · 2nd attempt (email match — not verified)"), and upper-casing a
 * sentence that long makes it unreadable. It WRAPS rather than truncates — an
 * ellipsis could cut off exactly the "(email match — not verified)" suffix the
 * whole design exists to keep on screen. An email-only chip wears a dashed
 * border as a SECOND cue; the words are the first.
 */
export function ReturningChip({ chip }: { chip: DealReturningChip }) {
  const tone = CHIP_TONE[chip.code] ?? UNKNOWN_CHIP_TONE;
  const style: CSSProperties = {
    ...chipStyle(tone),
    textTransform: 'none',
    letterSpacing: 0,
    fontWeight: 600,
    whiteSpace: 'normal',
    maxWidth: '100%',
    ...(chip.basis === 'email_unverified' ? { border: '1px dashed var(--gps-border-strong)', padding: '3px 9px' } : {}),
  };
  return (
    <span data-returning-chip={chip.code} data-match-basis={chip.basis} style={style}>
      {chip.label}
    </span>
  );
}

/**
 * The row of chips, or nothing at all when the card carries none.
 *
 * `asGroup` names the row as a "Returning patient" group — for the DRAWER.
 * The board card is itself a `<button>`, whose children are presentational in
 * ARIA, so a group role inside it would be announced by nothing and is left
 * off there (the chip words are part of the button's name either way).
 */
export function ReturningChipRow({
  card,
  style,
  asGroup = false,
}: {
  card: Pick<DealCard, 'returning'>;
  style?: CSSProperties;
  asGroup?: boolean;
}) {
  const chips = returningChips(card);
  if (chips.length === 0) return null;
  return (
    <span
      data-returning-chips
      {...(asGroup ? { role: 'group', 'aria-label': 'Returning patient' } : {})}
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', ...style }}
    >
      {chips.map((chip, index) => (
        <ReturningChip key={`${chip.code}-${index}`} chip={chip} />
      ))}
    </span>
  );
}

/* ── "Submitted as …" ──────────────────────────────────────────────────────── */

/** The typed name when the server sent one that differs, else null. */
export function submittedAsName(returning: DealReturning | undefined | null): string | null {
  const typed = returning?.submittedAs;
  if (!typed) return null;
  const name = [typed.firstName, typed.lastName]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(' ');
  return name || null;
}

/** The visible qualifier — part of the TEXT, never a tooltip (WCAG 1.4.13). */
export const SUBMITTED_AS_QUALIFIER = '(from this questionnaire, not verified)';

/**
 * "Submitted as Jane Jivraj (from this questionnaire, not verified)" — the
 * CTO's maiden/married-name rule: both names are hers, neither is corrected,
 * and the typed one shows beside the card's. The qualifier is visible text in
 * the same line, so it survives a screenshot, a screen reader and a phone.
 */
export function SubmittedAsLine({ card, style }: { card: Pick<DealCard, 'returning'>; style?: CSSProperties }) {
  const name = submittedAsName(card.returning);
  if (!name) return null;
  return (
    <span data-submitted-as style={{ fontSize: '12.5px', color: 'var(--gps-muted)', ...style }}>
      Submitted as <span style={{ color: 'var(--gps-text)', fontWeight: 600 }}>{name}</span> {SUBMITTED_AS_QUALIFIER}
    </span>
  );
}

/* ── The footer ────────────────────────────────────────────────────────────── */

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/**
 * "25 Sep 2026" — the footer's short date, so both halves fit one card line.
 * Built from a fixed month table rather than `Intl`'s `month: 'short'`, whose
 * en-GB output moved between ICU releases ("Sep" / "Sept"): one card must not
 * read differently in two browsers.
 */
export function shortUtcDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getUTCDate()} ${SHORT_MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/**
 * The card footer. "Updated 25 Sep 2026 · created 3 Sep 2026" once the server
 * says when something last happened; the old "Created 3 September 2026" —
 * byte-identical — when it does not (an unswept row, or an older server).
 * The board ORDER is the server's; this line only explains it.
 */
export function cardFooterText(card: Pick<DealCard, 'createdAt' | 'lastActivityAt'>): string {
  const updated = shortUtcDate(card.lastActivityAt);
  if (!updated) return `Created ${formatUtcDate(card.createdAt)}`;
  return `Updated ${updated} · created ${shortUtcDate(card.createdAt)}`;
}

/* ── Superseded vs Duplicate ───────────────────────────────────────────────── */

/**
 * The SYSTEM closed this card because a newer questionnaire arrived. `!= null`,
 * never truthiness: an absent key is a server that does not report supersedes.
 */
export function isSuperseded(card: Pick<DealCard, 'supersededByDealId'>): boolean {
  return card.supersededByDealId != null;
}

const SUPERSEDED_MARKER_LABEL = 'Superseded — the patient submitted a newer questionnaire';

/**
 * The `[Superseded]` marker, worn with the name exactly where `[Duplicate]` is
 * (the Asana-prefix model, duplicateSurface.tsx). A DIFFERENT WORD on purpose:
 * a staff duplicate means "a person decided these are the same journey"; a
 * supersede means "the patient started again and the system closed the older,
 * unpaid card". Reading one as the other sends an admin to the wrong undo.
 */
export function SupersededMarker({ style }: { style?: CSSProperties }) {
  return (
    <span
      role="img"
      aria-label={SUPERSEDED_MARKER_LABEL}
      title={SUPERSEDED_MARKER_LABEL}
      style={{ fontWeight: 700, fontSize: '12px', color: 'var(--gps-muted)', whiteSpace: 'nowrap', ...style }}
    >
      [Superseded]
    </span>
  );
}

/** The drawer's no-moves sentence for a superseded card — names the ADMIN reopen, never "un-mark". */
export const SUPERSEDED_NO_MOVES =
  'This card was closed because the patient submitted a newer questionnaire. An admin can reopen it if the two are separate journeys.';

/**
 * "Superseded by a newer questionnaire" — above the drawer's tabs, where the
 * staff-duplicate banner sits, and for the same reason: whatever tab this
 * opens on, the reader learns the work is on another card. `info`: nothing is
 * wrong. (clinic never supersedes a card with money held or taken, and
 * releases one the moment money arrives — but the banner states only what the
 * card itself says, so it makes no claim about money it has not read.)
 *
 * The ADMIN reopen rides the banner because it is the ONE undo for this state,
 * and a clinician gets the sentence without the button (R41 — the server's
 * `@Roles('ADMIN')` is the control; this is the affordance).
 */
export function SupersededBanner({
  onOpenNewer,
  onReopen,
}: {
  onOpenNewer?: () => void;
  /** Present for ADMIN only. */
  onReopen?: () => void;
}) {
  return (
    <Alert tone="info" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
      <span style={{ display: 'block', fontWeight: 700 }}>Superseded by a newer questionnaire</span>
      <span style={{ display: 'block', marginTop: '4px' }}>
        The patient started again, so this older card was closed. Nobody marked it as a duplicate. Work the
        newer card.
        {onOpenNewer ? (
          <>
            {' '}
            <DealLinkButton onClick={onOpenNewer} label="Open the newer card">
              Open the newer card
            </DealLinkButton>
          </>
        ) : null}
      </span>
      {onReopen ? (
        <Button
          type="button"
          variant="ghost"
          style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', marginTop: '8px' }}
          onClick={onReopen}
        >
          Reopen this card
        </Button>
      ) : null}
    </Alert>
  );
}

/* ── The drawer's returning summary ────────────────────────────────────────── */

/** The basis in words, for a row that is not a chip (the drawer lists). */
export const MATCH_BASIS_LABEL: Record<DealMatchBasis, string> = {
  same_account: 'Same account',
  same_journey: 'Same journey',
  email_unverified: 'Email match — not verified',
};

/**
 * THE HISTORY TAB'S "Previous deals" ROWS ARE EMAIL MATCHES (2026-09-26, S4.5
 * review). They are the contact's other cards, and clinic keeps one contact per
 * email address, so that is the only thing linking them — see the list in
 * DealDrawer.tsx. The words are the chips' own suffix, so the two surfaces say
 * the same thing about the same kind of match.
 */
export const SIBLING_BASIS_LABEL = 'email match — not verified';
export const SIBLING_BASIS_NOTE = `Other cards under this email address (${SIBLING_BASIS_LABEL}). A shared inbox can put someone else’s card here.`;

/**
 * "Returning patient" on the drawer's Overview — the chips again, plus the
 * contact's OTHER OPEN CARDS, which is what makes "Two live cards" and "Started
 * again" ACTIONABLE rather than merely stated (the reachability rule: a chip
 * that names another card must lead to it).
 *
 * ⚠️ AN EMAIL-ONLY ROW HAS NO ID AND GETS NO LINK. clinic nulls `dealId` on
 * every `email_unverified` row (S4.1 review, S-8): the card may be a stranger's
 * on a shared inbox, and this block rides every card for CLINICIAN. The row
 * still says the card exists and how it matched, and THIS BLOCK is not a route
 * in to it.
 *
 * ⚠️ BUT THE SAME CARD IS REACHABLE ELSEWHERE, AND THIS COMMENT USED TO SAY
 * OTHERWISE (corrected 2026-09-26, S4.5 review). Every card on the same
 * contact — and so the same email address — is in the History tab's "Previous
 * deals" list, and since S4.5 each of those rows has an "Open" link, with one
 * click and no audit row, labelled "(email match — not verified)"
 * (`SIBLING_BASIS_NOTE`). Board search matches on email too. The DP note's
 * "Ways round the logged click" records both; withholding the id here keeps
 * this summary from ADDING a route, it does not make the card unreachable.
 */
export function ReturningSummary({
  card,
  onOpenDeal,
}: {
  card: Pick<DealCard, 'returning'>;
  onOpenDeal?: (dealId: string) => void;
}) {
  const chips = returningChips(card);
  const others = Array.isArray(card.returning?.otherOpenDeals) ? card.returning.otherOpenDeals : [];
  const name = submittedAsName(card.returning);
  if (chips.length === 0 && others.length === 0 && !name) return null;
  const linked = others.filter((row) => row.dealId != null);
  const emailOnly = others.filter((row) => row.dealId == null);
  return (
    <section aria-label="Returning patient" style={{ marginTop: 'var(--gps-s-4)' }}>
      <h3 style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 700, color: 'var(--gps-text)' }}>Returning patient</h3>
      <ReturningChipRow card={card} asGroup />
      {others.length > 0 ? (
        <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'grid', gap: '6px' }} aria-label="Other open cards">
          {linked.map((row, index) => (
            <li key={row.dealId ?? index} style={{ fontSize: '13.5px', color: 'var(--gps-text)' }}>
              Another open card — {row.concluded ? 'questionnaire finished' : 'questionnaire not finished'} ·{' '}
              {MATCH_BASIS_LABEL[row.matchBasis] ?? row.matchBasis}
              {onOpenDeal && row.dealId ? (
                <>
                  {' '}
                  <DealLinkButton onClick={() => onOpenDeal(row.dealId as string)} label="Open the other open card">
                    Open it
                  </DealLinkButton>
                </>
              ) : null}
            </li>
          ))}
          {emailOnly.length > 0 ? (
            <li style={{ fontSize: '13.5px', color: 'var(--gps-muted)' }}>
              {emailOnly.length === 1
                ? '1 other open card under this email address'
                : `${emailOnly.length} other open cards under this email address`}{' '}
              (email match — not verified).
            </li>
          ) : null}
        </ul>
      ) : null}
    </section>
  );
}
